import { isUndefined } from "lodash";
import appConfig from "../../../appConfig";
import { TenantConfig } from "../../../platform/core/hooks/tenant-config";
import datasourceApiManager from "../../../platform/services/api/DatasourceApiService";
import { ApptuitDatasource } from "../../../platform/services/datasources/apptuit/ApptuitDatasource";
import { DruidIngestionConfig } from "../../traces/Analytics/types";
import { DefaultConfig, Org, Role } from "../../../platform/core";
import { InceptionResponse } from "../../../platform/services/api/types";
import { BootConfigPayload } from "../../../platform/services/api/UIService/types";
import {
  AddtenantRequest,
  verifyOtpForDropIngestionRequest,
  DruidIngestionConfigResponse,
  GenerateOtpForDropIngestionRequest,
  ShardsResponse,
  Tenant,
  TenantCustomConfiguration,
  TenantDeleteDataStatusResponse,
  UserHome,
  TenantDeleteDataStatusRequest,
  ListSwitchOrgsResponse,
  BusinessHierarchy,
  CurrentFocusArea,
  LoginRequestV2
} from "./types";

interface Result<T> {
  data: T;
  error: boolean;
  message: string;
}

interface ResponseMessage {
  message: string;
}

class TenantApiService {
  readonly NAMESPACE: string = "ui";
  readonly CONFIG_NAME: string = "tenant_config";

  private datasource: ApptuitDatasource;

  async generateOtpForDropIngestion(tenantId: string, payload: GenerateOtpForDropIngestionRequest) {
    const url = `/dms/api/dms/generateOTP/${tenantId}`;

    const result: Result<ResponseMessage> = {
      data: { message: "" },
      error: false,
      message: ""
    };

    try {
      this.init();
      const response = await this.datasource.post<any, GenerateOtpForDropIngestionRequest>(url, payload);
      result.data = response.data;
      result.message = response.statusText;
    } catch (err) {
      result.error = true;
      result.message = (err as any).response.data.message;
    }
    return result;
  }

  async verifyOtpForDropIngestion(tenantId: string, payload: verifyOtpForDropIngestionRequest) {
    const url = `/dms/api/dms/drop/${tenantId}/data`;

    const result: Result<ResponseMessage> = {
      data: { message: "" },
      error: false,
      message: ""
    };

    try {
      this.init();
      const response = await this.datasource.post<any, verifyOtpForDropIngestionRequest>(url, payload);
      result.data = response.data;
      result.message = response.statusText;
    } catch (err) {
      result.error = true;
      result.message = (err as any).response.data.message;
    }
    return result;
  }

  async getDeleteDataStatus(tenantId: string, payload: TenantDeleteDataStatusRequest) {
    const url = `/dms/api/dms/drop/${tenantId}/status`;

    const result: Result<TenantDeleteDataStatusResponse> = {
      data: null,
      error: false,
      message: ""
    };

    try {
      this.init();
      const response = await this.datasource.post<TenantDeleteDataStatusResponse, TenantDeleteDataStatusRequest>(
        url,
        payload
      );
      result.data = response.data;
      result.message = response.statusText;
    } catch (err) {
      result.error = true;
      result.message = (err as any).response.data.message;
    }
    return result;
  }

  async getTenants(): Promise<Result<Tenant[]>> {
    const result: Result<Tenant[]> = {
      data: [],
      error: false,
      message: ""
    };
    const url = "/api/tenants";
    try {
      this.init();
      const response = await this.datasource.get<Tenant[], unknown>(url, null, {
        params: { includeTenantConfig: true }
      });
      result.data = response.data;
      result.message = response.statusText;
    } catch (err) {
      result.error = true;
      result.message = (err as any).response?.data.message;
    }
    return result;
  }

  async getCurrentTenant(): Promise<Result<Tenant>> {
    const result: Result<Tenant> = {
      data: null,
      error: false,
      message: ""
    };
    const url = "/api/tenants/current";
    try {
      this.init();
      const response = await this.datasource.get<Tenant, null>(url);
      result.data = response.data;
      result.message = response.statusText;
    } catch (err) {
      result.error = true;
      result.message = (err as any).response?.data.message;
    }
    return result;
  }

  async addTenant(data: AddtenantRequest): Promise<Result<ResponseMessage>> {
    const result: Result<ResponseMessage> = {
      data: { message: "" },
      error: false,
      message: ""
    };
    const url = "/api/tenants";
    try {
      this.init();
      const response = await this.datasource.post<ResponseMessage, AddtenantRequest>(url, data);
      result.data = response.data;
      result.message = result.data.message;
    } catch (err) {
      result.error = true;
      result.message = (err as any).response.data.message;
    }
    return result;
  }

  async renameTenant(tenantId: string, newTenantName: string): Promise<Result<ResponseMessage>> {
    const result: Result<ResponseMessage> = {
      data: { message: "" },
      error: false,
      message: ""
    };

    const url = `/api/tenants/${tenantId}/name/${newTenantName}`;

    try {
      this.init();
      const response = await this.datasource.put<ResponseMessage, null>(url);
      result.data = response.data;
      result.message = response.statusText;
    } catch (err) {
      result.error = true;
      result.message = (err as any).response.data.message;
    }
    return result;
  }

  async syncUsers(tenantId: string): Promise<Result<ResponseMessage>> {
    const result: Result<ResponseMessage> = {
      data: { message: "" },
      error: false,
      message: ""
    };

    const url = `/api/tenants/users/migrate/${tenantId}`;

    try {
      this.init();
      const response = await this.datasource.post<ResponseMessage, null>(url);
      result.data = response.data;
      result.message = response.statusText;
    } catch (err) {
      result.error = true;
      result.message = (err as any).response.data.message;
    }
    return result;
  }

  async getShards(): Promise<Result<ShardsResponse>> {
    const result: Result<ShardsResponse> = {
      data: {
        i_anomMetricsShard: [],
        i_metricsShard: [],
        i_tracesShard: []
      },
      error: false,
      message: ""
    };
    const url = "/api/tenants/shards";
    try {
      this.init();
      const response = await this.datasource.get<ShardsResponse, null>(url);
      result.data = response.data;
      result.message = response.statusText;
    } catch (err) {
      result.error = true;
      result.message = (err as any).response?.data.message;
    }
    return result;
  }

  async getUserHome(): Promise<InceptionResponse<UserHome>> {
    const url = `/api/tenants/users/current-user/home`;
    this.init();
    return await this.datasource.get<UserHome, null>(url);
  }

  async updateTenantConfig(tenantConfig: TenantConfig): Promise<string> {
    const url = `/api/tenants/current/config/${this.NAMESPACE}/${this.CONFIG_NAME}`;
    this.init();
    type UpdateResponse = {
      Status: string;
    };
    const response = await this.datasource.put<UpdateResponse, TenantConfig>(url, tenantConfig);
    return response.data.Status;
  }
  getDefaultConfig(role: Role): Promise<DefaultConfig[]> {
    if (role === Role.Guest || appConfig.anomShareId) {
      return Promise.resolve(this.getMockDefaultConfig());
    } else {
      return this.getCurrentDefaultConfig();
    }
  }

  // Legacy methods - keeping for backward compatibility if needed elsewhere
  getTenantConfig(role: Role): Promise<TenantConfig> {
    if (role === Role.Guest || appConfig.anomShareId) {
      return this.getMockTenantConfig();
    } else {
      return this.getCurrentTenantConfig();
    }
  }

  getTenantDefaultTimezone(role: Role): Promise<Result<string>> {
    if (role === Role.Guest || appConfig.anomShareId) {
      return this.getMockTenantTimezone();
    } else {
      return this.getCurrentTenantTimezone();
    }
  }

  getTenantDefaultCurrency(role: Role): Promise<Result<string>> {
    if (role === Role.Guest || appConfig.anomShareId) {
      return this.getMockTenantCurrency();
    } else {
      return this.getCurrentCurrency();
    }
  }

  async getDruidIngestionConfig(): Promise<DruidIngestionConfig> {
    const url = "/api/tenants/system/config/druid/ingestion";
    this.init();
    const response = await this.datasource.get<DruidIngestionConfigResponse, null>(url);
    const data = response?.data;
    return {
      trace: isUndefined(data?.__trace) ? true : data.__trace,
      event: isUndefined(data?.__event) ? true : data.__event,
      preview: isUndefined(data?.__preview) ? true : data.__preview,
      sketch: isUndefined(data?.__sketch) ? true : data.__sketch
    };
  }

  async fetchAllConfigs(tenantId: string): Promise<Result<TenantCustomConfiguration[]>> {
    const result: Result<TenantCustomConfiguration[]> = {
      data: [],
      error: false,
      message: ""
    };

    const url = `/api/tenants/${tenantId}/configs`;

    try {
      this.init();
      const response = await this.datasource.get<TenantCustomConfiguration[], null>(url);
      result.data = response.data;
      result.message = response.statusText;
    } catch (err) {
      result.error = true;
      result.message = (err as any).response.data.message;
    }
    return result;
  }

  async createConfig(tenantId: string, config: TenantCustomConfiguration): Promise<Result<ResponseMessage>> {
    return await this.createOrUpdateConfig(tenantId, config);
  }

  async updateConfig(tenantId: string, config: TenantCustomConfiguration): Promise<Result<ResponseMessage>> {
    return await this.createOrUpdateConfig(tenantId, config);
  }

  async deleteConfig(tenantId: string, config: TenantCustomConfiguration): Promise<Result<ResponseMessage>> {
    const result: Result<ResponseMessage> = {
      data: null,
      error: false,
      message: ""
    };

    const url = config.namespace
      ? `/api/tenants/${tenantId}/config/${config.namespace}/${config.name}`
      : `/api/tenants/${tenantId}/config/${config.name}`;
    try {
      this.init();
      const response = await this.datasource.delete(url);
      result.message = response.statusText;
    } catch (err) {
      result.error = true;
      result.message = (err as any).response.data.message;
    }
    return result;
  }

  async getBootConfig(): Promise<BootConfigPayload> {
    const url = "/api/tenants/users/current-user/boot-config";
    this.init();
    const response = await this.datasource.get<BootConfigPayload, null>(url);
    return response.data;
  }

  async getOrgsForSwitchTenant(): Promise<ListSwitchOrgsResponse> {
    const url = "/api/tenants/users/current-user/list-tenants";
    this.init();
    const response = await this.datasource.get<ListSwitchOrgsResponse, null>(url);

    response.data.userTenants.forEach(org => {
      org.name = org.tenantInfo.name || "";
      org.orgId = Number(org.tenantInfo.orgId);
    });
    return response.data;
  }

  async getOrgs(): Promise<Org[]> {
    const url = "/api/tenants/users/current-user/tenants";
    this.init();
    const response = await this.datasource.get<Org[], null>(url);
    return response.data;
  }

  private init() {
    this.datasource = datasourceApiManager.getDefault();
  }

  private async getCurrentDefaultConfig(): Promise<DefaultConfig[]> {
    const url = `/api/tenants/current/configs`;
    this.init();
    const response = await this.datasource.get<DefaultConfig[], null>(url);
    return response.data;
  }

  private getMockDefaultConfig(): Promise<DefaultConfig[]> {
    return Promise.resolve([
      {
        name: "currency",
        config: "USD"
      },
      {
        namespace: "ui",
        name: "tenant_config",
        config: mockTenantConfig as TenantConfig
      },
      {
        name: "defaultTimeZone",
        config: ""
      }
    ]);
  }

  private async getCurrentCurrency(): Promise<Result<string>> {
    const result: Result<string> = {
      data: "",
      error: false,
      message: ""
    };
    const url = `/api/tenants/current/config/currency`;

    try {
      this.init();
      const response = await this.datasource.get<string, null>(url);
      result.data = response.data;
      result.message = response.statusText;
    } catch (err) {
      result.error = true;
      result.message = (err as any).response?.data.message;
    }
    return result;
  }

  private getMockTenantCurrency(): Promise<Result<string>> {
    return Promise.resolve({
      data: "USD",
      error: false,
      message: ""
    });
  }

  private async getCurrentTenantTimezone(): Promise<Result<string>> {
    const result: Result<string> = {
      data: "",
      error: false,
      message: ""
    };
    const url = `/api/tenants/current/config/defaultTimeZone`;

    try {
      this.init();
      const response = await this.datasource.get<string, null>(url);
      result.data = response.data;
      result.message = response.statusText;
    } catch (err) {
      result.error = true;
      result.message = (err as any).response?.data.message;
    }
    return result;
  }

  private getMockTenantTimezone(): Promise<Result<string>> {
    return Promise.resolve({
      data: "UTC",
      error: false,
      message: ""
    });
  }

  private async getCurrentTenantConfig(): Promise<TenantConfig> {
    const url = `/api/tenants/current/config/${this.NAMESPACE}/${this.CONFIG_NAME}`;
    this.init();
    const response = await this.datasource.get<TenantConfig, null>(url);
    return response.data;
  }

  private getMockTenantConfig(): Promise<TenantConfig> {
    return Promise.resolve(mockTenantConfig as TenantConfig);
  }

  private async createOrUpdateConfig(
    tenantId: string,
    config: TenantCustomConfiguration
  ): Promise<Result<ResponseMessage>> {
    const result: Result<ResponseMessage> = {
      data: { message: "" },
      error: false,
      message: ""
    };
    const url = config.namespace
      ? `/api/tenants/${tenantId}/config/${config.namespace}/${config.name}`
      : `/api/tenants/${tenantId}/config/${config.name}`;
    try {
      this.init();
      const response = await this.datasource.put<ResponseMessage, TenantCustomConfiguration["config"]>(
        url,
        config.config
      );
      result.data = response.data;
      result.message = response.statusText;
    } catch (err) {
      result.error = true;
      result.message = (err as any).response.data.message;
    }

    return result;
  }

  async performFreemiumLogin(clientId: string) {
    const result: Result<any> = {
      data: null,
      error: false,
      message: ""
    };
    try {
      const response = await this.datasource.get(`/api/tenants/login/freemium?clientId=${clientId}`);

      result.data = response.data;
      result.message = response.statusText;
    } catch (err) {
      result.error = true;
      result.message = (err as any).message;
    }
    return result;
  }

  async performFreemiumLoginWithToken(payload: LoginRequestV2) {
    const { loginType, magicCode } = payload;
    const result: Result<any> = {
      data: null,
      error: false,
      message: ""
    };
    try {
      const response = await this.datasource.post(`/api/tenants/v2/login`, {
        loginType,
        magicCode
      });

      result.data = response.data;
      result.message = response.statusText;
    } catch (err) {
      result.error = true;
      result.message = (err as any).message;
    }
    return result;
  }

  async inititeSignUpFlow(emailId: string) {
    const result: Result<any> = {
      data: null,
      error: false,
      message: ""
    };

    try {
      const url = "/api/tenants/users/signup";

      const response = await this.datasource.post(url, { email: emailId });
      result.data = response.data;
      result.message = response.statusText;
    } catch (e) {
      result.error = true;
      result.message = (e as any).response.data.msg;
    }

    return result;
  }

  async validateInviteCode(inviteCode: string) {
    const result: Result<any> = {
      data: null,
      error: false,
      message: ""
    };

    const url = `/api/tenants/login/invite/${inviteCode}`;
    try {
      const response = await this.datasource.get(url);
      result.data = response.data;
    } catch (e) {
      result.error = true;
      result.message = (e as any).message;
    }

    return result;
  }

  async validateInviteCodeV2(inviteCode: string) {
    const result: Result<any> = {
      data: null,
      error: false,
      message: ""
    };

    const url = `/api/tenants/v2/login/invite/${inviteCode}`;
    try {
      const response = await this.datasource.get(url);
      result.data = response.data;
    } catch (e) {
      result.error = true;
      result.message = (e as any).message;
    }

    return result;
  }

  async sendResetPasswordEmailV2(email: string) {
    const result: Result<any> = {
      data: null,
      error: false,
      message: ""
    };

    const url = `/api/tenants/v2/login/send-reset-email?login=${email}`;
    try {
      const response = await this.datasource.post(url);
      result.data = response.data;
    } catch (e) {
      result.error = true;
      result.message = (e as any).response.data.msg;
    }

    return result;
  }

  async validateResetPasswordTokenV2(token: string) {
    const result: Result<any> = {
      data: null,
      error: false,
      message: ""
    };

    const url = `/api/tenants/v2/login/reset/${token}`;
    try {
      const response = await this.datasource.get(url);
      result.data = response.data;
    } catch (e) {
      result.error = true;
      result.message = (e as any).message;
    }

    return result;
  }

  async initiateSSOLogin(provider: string, redirectUrl = "") {
    const encodedRedirectUrl = redirectUrl ? encodeURIComponent(redirectUrl) : "";
    const url = `/api-proxy/api/tenants/auth/login/${provider}?redirectUrl=${encodedRedirectUrl}`;
    window.location.href = url;
    await Promise.resolve();
  }

  async loginWithPasswordV2(username: string, password: string) {
    const requestPaylod = {
      login: username,
      password: password,
      userNamePassword: {
        login: username,
        password: password
      }
    };
    const result: Result<any> = {
      data: null,
      error: false,
      message: ""
    };

    try {
      const response = await this.datasource.post("/api/tenants/v2/login", requestPaylod);
      result.data = response.data;
    } catch (e) {
      result.error = true;
      result.data = (e as any).response.data;
      result.message = (e as any).response.data.message;
    }

    return result;
  }

  async setUpPasswordAndLoginV2(inviteCode: string, password: string) {
    const result: Result<any> = {
      data: null,
      error: false,
      message: ""
    };

    const url = `/api/tenants/v2/login/set-password/${inviteCode}`;

    try {
      const response = await this.datasource.post(url, {
        newPassword: password
      });
      result.data = response.data;
    } catch (e) {
      result.error = true;
      result.message = (e as any).response.data.message;
    }
    return result;
  }

  async setResettedPasswordV2(token: string, password: string) {
    const result: Result<any> = {
      data: null,
      error: false,
      message: ""
    };

    const url = `/api/tenants/v2/login/reset/${token}`;

    try {
      const response = await this.datasource.post(url, {
        newPassword: password
      });
      result.data = response.data;
    } catch (e) {
      result.error = true;
      result.message = (e as any).response.data.message;
    }
    return result;
  }

  async logout() {
    const result: Result<any> = {
      data: null,
      error: false,
      message: ""
    };
    try {
      const response = await this.datasource.delete("/api/tenants/login");
      result.data = response.data;
    } catch (e) {
      result.error = true;
      result.message = (e as any).response.data.message;
    }
    return result;
  }

  async updateCurrentUserInfo(payload: CurrentFocusArea): Promise<Result<ResponseMessage>> {
    const result: Result<ResponseMessage> = {
      data: { message: "" },
      error: false,
      message: ""
    };

    const url = "/api/tenants/users/current-user/currentFocusArea";

    try {
      this.init();
      const response = await this.datasource.put<ResponseMessage, CurrentFocusArea>(url, payload);
      result.data = response.data;
      result.message = response.statusText;
    } catch (err) {
      result.error = true;
      result.message = (err as any).response?.data?.message || (err as any).message;
    }
    return result;
  }

  async updateBizHierarchy(payload: BusinessHierarchy): Promise<Result<ResponseMessage>> {
    const result: Result<ResponseMessage> = {
      data: { message: "" },
      error: false,
      message: ""
    };

    const url = "/api/tenants/users/current-user/business_hierarchy";

    try {
      this.init();
      const response = await this.datasource.put<ResponseMessage, BusinessHierarchy>(url, payload);
      result.data = response.data;
      result.message = response.statusText;
    } catch (err) {
      result.error = true;
      result.message = (err as any).response?.data?.message || (err as any).message;
    }
    return result;
  }
}

const tenantApiService = new TenantApiService();
export { tenantApiService };

const mockTenantConfig = {
  demoTenant: false,
  leftNavPreset: [] as any[],
  defaultDrilldownVersion: "v5",
  disableNewDashboardFilterPanel: false
};
